import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { flushGenericQuestionnaire } from 'actions/genericQuestionnaire';
import routes from 'constants/routes';
import {
  ExpatDependentQuestionnaire,
  ExpatQuestionnaire,
} from 'features/expat/models';
import { CustomComponentProps } from 'models/questionnaireFramework';
import { useDispatch } from 'react-redux';
import { generatePath, useHistory } from 'react-router';
import endpoint from 'shared/api';
import { useSafeTranslation } from 'shared/i18n';
import { RequestProcessing } from 'SignupQuestionnaire/components/Processing/RequestProcessing';

export const QuoteProcessing = ({
  questionnaireAnswers,
  onSubmitValue,
}: CustomComponentProps<ExpatQuestionnaire | ExpatDependentQuestionnaire>) => {
  const { t } = useSafeTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { mutateAsync: submitQuotes } = useMutation({
    mutationFn: () => {
      const { dateOfBirth } = questionnaireAnswers;

      if (!dateOfBirth) {
        throw new Error('Missing fields required to fetch Expat quote');
      }

      return endpoint.createExpatQuotes(dateOfBirth);
    },
    onSuccess: ({ data: quotes }) => {
      onSubmitValue({ options: quotes });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'QUOTE',
          vertical: 'EXPAT',
        },
        extra: {
          description: 'Error happened while fetching Expat quotes',
        },
      });
    },
  });

  const textList: string[] = [
    t('expat.qnr.quote.processing.loadingText', 'Processing quote...'),
  ];

  const handleErrorRecovery = () => {
    if ('mainPolicyId' in questionnaireAnswers) {
      const { mainPolicyId } = questionnaireAnswers;
      dispatch(flushGenericQuestionnaire('expat'));
      const redirectPath = generatePath(routes.policies.expat.dependents.path, {
        mainPolicyId,
      });
      history.push(redirectPath);
    } else {
      dispatch(flushGenericQuestionnaire('expat'));
      history.push(routes.policies.expat.path);
    }
  };

  return (
    <RequestProcessing
      textList={textList}
      requestFn={submitQuotes}
      handleErrorRecovery={handleErrorRecovery}
      shouldSubmitScreen={false}
    />
  );
};
